/**
 * The cell list behind the audit summary expander.
 *
 * Holds the layout of the 18 audit fields and the fallbacks and visibility gates each one
 * needs, apart from the component that renders them, so `AuditSummaryExpander` stays a
 * renderer. Derivation is split from building on purpose: the field count alone carries
 * enough nullish coalescing to push either half over the cognitive-complexity cap.
 *
 * A consumer wanting a subset picks it with `selectAuditSummaryCells` rather than
 * rebuilding the list, so every caller shows the same field the same way.
 */
import { BadgeType } from '@amzn/stencil-react-components/badge';
import { AuditStatus } from '@amzn/gbsa-helios-typescript-client';

import { formatCodeForDisplay } from '../../../shared/domain';
import {
  AUDIT_TYPE_DISPLAY_LABEL,
  STATUS_BADGE_TYPE,
  STATUS_DISPLAY_LABEL,
} from '../../../shared/ui/ListCard';
import type { SummaryCellConfig } from '../../../shared/ui/SummaryExpander';
import { formatAuditDate, formatScorePercent } from '../logic';
import {
  AUDIT_SUMMARY_FIELD as FIELD,
  AUDIT_SUMMARY_MESSAGE as MESSAGE,
  type AuditSummaryAudit,
  type AuditSummaryFieldKey,
  type AuditSummaryTranslator,
} from './auditSummaryCellKeys';

// Stable empty default — hoisted so the `?? EMPTY_ASSIGNEES` fallback
// passes the same array identity every render (scanner 5b: no inline
// `?? []` in a place that feeds a reference-typed prop). Cast through
// `as const` keeps the tuple literal read-only without pulling in
// Object.freeze (the workspace lint rule bans it in favor of readonly
// types, since the runtime freeze is redundant here — no caller ever
// mutates the array).
const EMPTY_ASSIGNEES: readonly string[] = [] as const;

/**
 * Shape of the pre-computed derived values passed into
 * {@link buildAuditSummaryCells}. Splitting derivation from cell
 * construction is what keeps both functions under the cognitive
 * complexity cap — one owns the fallbacks and visibility flags, the
 * other owns the layout of the resulting cell list.
 */
export interface AuditSummaryDerived {
  statusBadgeType: BadgeType;
  statusLabel: string;
  auditTypeLabel: string;
  isReopened: boolean;
  assigneesList: readonly string[];
  showScore: boolean;
  showCompleted: boolean;
  showCompletedBy: boolean;
  showReopenedReason: boolean;
  showReopenedAt: boolean;
  scorePercent: number;
  businessUnitValue: string;
  divisionValue: string;
  templateIdValue: string;
  templateValue: string;
  completedByValue: string;
  createdByValue: string;
  reopenedByValue: string;
  reopenedReasonValue: string;
}

/** Pull the display labels out with per-field fallbacks. */
function getAuditSummaryLabels(
  audit: AuditSummaryAudit,
): Pick<AuditSummaryDerived, 'statusBadgeType' | 'statusLabel' | 'auditTypeLabel'> {
  return {
    statusBadgeType: STATUS_BADGE_TYPE[audit.status] ?? BadgeType.Neutral,
    statusLabel: STATUS_DISPLAY_LABEL[audit.status] ?? audit.status,
    // Unknown auditType → fall back to the raw enum value. Visible
    // bug signal (e.g. a new backend enum value the front-end hasn't
    // learned yet) rather than a silent blank cell.
    auditTypeLabel: AUDIT_TYPE_DISPLAY_LABEL[audit.auditType] ?? audit.auditType,
  };
}

/** Pre-compute the boolean `show` flags every conditional cell reads. */
function getAuditSummaryVisibility(
  audit: AuditSummaryAudit,
): Pick<
  AuditSummaryDerived,
  | 'isReopened'
  | 'showScore'
  | 'showCompleted'
  | 'showCompletedBy'
  | 'showReopenedReason'
  | 'showReopenedAt'
> {
  const isCompleted = audit.status === AuditStatus.COMPLETED;
  const isReopened = !!audit.reopenedBy;
  return {
    isReopened,
    showScore: isCompleted && audit.metrics?.scorePercent != null,
    showCompleted: isCompleted && audit.completionDate != null,
    showCompletedBy: isCompleted && !!audit.completedBy,
    showReopenedReason: isReopened && !!audit.reopenedReason,
    showReopenedAt: isReopened && audit.reopenedDate != null,
  };
}

/** Pre-compute the string / number values with null-safe fallbacks. */
function getAuditSummaryValues(
  audit: AuditSummaryAudit,
): Pick<
  AuditSummaryDerived,
  | 'assigneesList'
  | 'scorePercent'
  | 'businessUnitValue'
  | 'divisionValue'
  | 'templateIdValue'
  | 'templateValue'
  | 'completedByValue'
  | 'createdByValue'
  | 'reopenedByValue'
  | 'reopenedReasonValue'
> {
  return {
    assigneesList: audit.assignees ?? EMPTY_ASSIGNEES,
    scorePercent: audit.metrics?.scorePercent ?? 0,
    // BU + division are stored as underscore-joined codes (e.g.
    // `NORTH_AMERICA_BU`). `formatCodeForDisplay` swaps underscores
    // for spaces so the browser can wrap the value on word
    // boundaries — without it a long code renders as one unbreakable
    // token that overflows the summary cell on mobile. The raw
    // strings still flow into query keys / API inputs via other
    // paths (useAuditDetailViolations, useAuditDetailTasks, etc.)
    // which read audit.businessUnit / audit.divisionName directly.
    businessUnitValue: formatCodeForDisplay(audit.businessUnit),
    divisionValue: formatCodeForDisplay(audit.divisionName),
    templateIdValue: audit.templateId ?? '',
    templateValue: audit.templateName ?? '',
    completedByValue: audit.completedBy ?? '',
    createdByValue: audit.createdBy ?? '',
    reopenedByValue: audit.reopenedBy ?? '',
    reopenedReasonValue: audit.reopenedReason ?? '',
  };
}

/**
 * Derive every fallback / visibility flag the cell builder needs by
 * merging the three sub-helpers. Splitting is what keeps each function
 * under the workspace cognitive-complexity cap — the audit summary
 * accumulates a lot of nullish coalescing and short-circuit logic
 * across 18 fields, and grouping by concern (labels vs. visibility vs.
 * fallback values) makes each seam trivial to reason about.
 */
export function deriveAuditSummaryValues(audit: AuditSummaryAudit): AuditSummaryDerived {
  return {
    ...getAuditSummaryLabels(audit),
    ...getAuditSummaryVisibility(audit),
    ...getAuditSummaryValues(audit),
  };
}

/**
 * Pure builder for the summary cell list. Reads every fallback / gate
 * out of the pre-derived {@link AuditSummaryDerived} bag so the
 * function itself stays layout-focused — the cognitive complexity is
 * dominated by the number of cells, not by branching logic.
 */
export function buildAuditSummaryCells(
  audit: AuditSummaryAudit,
  t: AuditSummaryTranslator,
  d: AuditSummaryDerived,
): SummaryCellConfig[] {
  return [
    {
      key: FIELD.auditId,
      show: true,
      label: t(MESSAGE.labelAuditId),
      value: audit.auditId,
    },
    {
      key: FIELD.status,
      show: true,
      badge: true,
      label: t(MESSAGE.labelStatus),
      badgeType: d.statusBadgeType,
      badgeValue: d.statusLabel,
    },
    {
      key: FIELD.score,
      show: d.showScore,
      label: t(MESSAGE.labelScore),
      value: formatScorePercent(d.scorePercent),
    },
    {
      key: FIELD.location,
      show: true,
      label: t(MESSAGE.labelLocation),
      value: audit.location,
    },
    {
      key: FIELD.businessUnit,
      show: d.businessUnitValue.length > 0,
      label: t(MESSAGE.labelBusinessUnit),
      // Raw BU id — resolving to the human-readable BU name requires
      // an extra list fetch (`useBusinessUnitsQuery`) that the summary
      // panel currently doesn't own. Show the id so the field isn't
      // empty; a follow-up can wire the name resolver.
      value: d.businessUnitValue,
    },
    {
      key: FIELD.division,
      show: d.divisionValue.length > 0,
      label: t(MESSAGE.labelDivision),
      value: d.divisionValue,
    },
    {
      key: FIELD.auditType,
      show: true,
      label: t(MESSAGE.labelAuditType),
      value: d.auditTypeLabel,
    },
    {
      key: FIELD.templateId,
      show: d.templateIdValue.length > 0,
      label: t(MESSAGE.labelTemplateId),
      value: d.templateIdValue,
    },
    {
      key: FIELD.template,
      show: d.templateValue.length > 0,
      label: t(MESSAGE.labelTemplate),
      value: d.templateValue,
    },
    {
      key: FIELD.assignees,
      show: true,
      assignees: true,
      label: t(MESSAGE.labelAssignedTo),
      assigneesList: d.assigneesList,
      emptyLabel: t(MESSAGE.unassigned),
    },
    {
      key: FIELD.scheduledDate,
      show: audit.scheduledDate != null,
      label: t(MESSAGE.labelScheduledDate),
      value: formatAuditDate(audit.scheduledDate),
    },
    {
      key: FIELD.startDate,
      show: true,
      label: t(MESSAGE.labelStartDate),
      value: formatAuditDate(audit.startDate),
    },
    {
      key: FIELD.expiryDate,
      show: true,
      label: t(MESSAGE.labelExpiryDate),
      value: formatAuditDate(audit.expirationDate),
    },
    {
      key: FIELD.completed,
      show: d.showCompleted,
      label: t(MESSAGE.labelCompleted),
      value: formatAuditDate(audit.completionDate),
    },
    {
      // completedBy is written by the completion workflow; a legacy
      // record may carry a stray value even on non-terminal audits.
      // Suppress the cell unless the audit is actually COMPLETED so
      // the summary panel doesn't advertise a completion on an
      // in-progress audit.
      key: FIELD.completedBy,
      show: d.showCompletedBy,
      label: t(MESSAGE.labelCompletedBy),
      value: d.completedByValue,
    },
    {
      key: FIELD.createdBy,
      show: d.createdByValue.length > 0,
      label: t(MESSAGE.labelCreatedBy),
      value: d.createdByValue,
    },
    {
      key: FIELD.reopenedBy,
      show: d.isReopened,
      label: t(MESSAGE.labelReopenedBy),
      value: d.reopenedByValue,
    },
    {
      key: FIELD.reopenedReason,
      show: d.showReopenedReason,
      label: t(MESSAGE.labelReopenedReason),
      value: d.reopenedReasonValue,
    },
    {
      key: FIELD.reopenedAt,
      show: d.showReopenedAt,
      label: t(MESSAGE.labelReopenedAt),
      value: formatAuditDate(audit.reopenedDate),
    },
  ];
}

/**
 * Narrow the cell list to `fields`, in the order `fields` gives rather
 * than the order the cells were built in.
 *
 * An allowlisted key the audit does not carry drops out here. Its `show`
 * flag is still honoured downstream, so a requested cell with no value
 * stays hidden rather than rendering an empty label.
 */
export function selectAuditSummaryCells(
  cells: SummaryCellConfig[],
  fields: readonly AuditSummaryFieldKey[],
): SummaryCellConfig[] {
  const byKey = new Map(cells.map((cell) => [cell.key, cell]));
  return fields
    .map((field) => byKey.get(field))
    .filter((cell): cell is SummaryCellConfig => cell !== undefined);
}
